import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '../../components/ui/button';
import { trpc } from '../../lib/trpc';

type CallbackSearch = {
  code: string | undefined;
  state: string | undefined;
  error: string | undefined;
  // biome-ignore lint/style/useNamingConvention: OAuth callback query parameter
  error_description: string | undefined;
};

export const Route = createFileRoute('/open-banking/callback')({
  validateSearch: (search: Record<string, unknown>): CallbackSearch => ({
    code: typeof search.code === 'string' ? search.code : undefined,
    state: typeof search.state === 'string' ? search.state : undefined,
    error: typeof search.error === 'string' ? search.error : undefined,
    // biome-ignore lint/style/useNamingConvention: OAuth callback query parameter
    error_description:
      typeof search.error_description === 'string' ? search.error_description : undefined,
  }),
  component: CallbackPage,
});

const STATUS = {
  loading: { title: 'Ansluter bank…', icon: Loader2, className: 'bg-muted text-muted-foreground' },
  success: {
    title: 'Banken är ansluten!',
    icon: CheckCircle2,
    className: 'bg-positive-soft text-positive',
  },
  error: { title: 'Något gick fel', icon: XCircle, className: 'bg-negative-soft text-negative' },
} as const;

function CallbackPage() {
  const search = useSearch({ from: '/open-banking/callback' });
  const navigate = useNavigate();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [errorMsg, setErrorMsg] = useState('');
  const calledRef = useRef(false);
  // Sessions started from a shared connect link (no Budgie login) use a "link_" state
  const isLinkFlow = search.state?.startsWith('link_') ?? false;

  const completeLinkAuth = trpc.enableBanking.completeLinkAuth.useMutation({
    onSuccess: () => setStatus('success'),
    onError: (err) => {
      setStatus('error');
      setErrorMsg(err.message);
    },
  });

  const completeAuth = trpc.enableBanking.completeAuth.useMutation({
    onSuccess: () => {
      setStatus('success');
      setTimeout(() => {
        void navigate({ to: '/open-banking' });
      }, 1500);
    },
    onError: (err) => {
      // This page is public (for connect links); send logged-out users to login
      if (err.data?.code === 'UNAUTHORIZED') {
        void navigate({ to: '/login' });
        return;
      }
      setStatus('error');
      setErrorMsg(err.message);
    },
  });

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    if (search.error) {
      setStatus('error');
      setErrorMsg(search.error_description ?? search.error);
      return;
    }

    if (!search.code || !search.state) {
      setStatus('error');
      setErrorMsg('Saknar auktoriseringskod eller state-parameter.');
      return;
    }

    if (isLinkFlow) {
      completeLinkAuth.mutate({ code: search.code, state: search.state });
    } else {
      completeAuth.mutate({ code: search.code, state: search.state });
    }
  }, [search, completeAuth, completeLinkAuth, isLinkFlow]);

  const view = STATUS[status];
  const Icon = view.icon;

  return (
    <div className="mx-auto mt-8 max-w-sm sm:mt-20">
      <div className="rounded-2xl border border-border bg-card p-6 text-center sm:p-7">
        <span
          aria-hidden
          className={cn(
            'mx-auto flex size-12 items-center justify-center rounded-full',
            view.className,
          )}
        >
          <Icon className={cn('size-6', status === 'loading' && 'animate-spin')} />
        </span>
        <h1 className="mt-4 text-xl font-semibold tracking-tight">{view.title}</h1>

        {status === 'loading' && (
          <p className="mt-1 text-sm text-muted-foreground">Väntar på svar från banken…</p>
        )}
        {status === 'success' && (
          <p className="mt-1 text-sm text-muted-foreground">
            {isLinkFlow
              ? `${completeLinkAuth.data?.accountCount ?? 0} konton anslutna. Du kan stänga den här sidan.`
              : 'Omdirigerar till bankkopplingar…'}
          </p>
        )}
        {status === 'error' && (
          <>
            <p className="mt-1 text-sm text-negative">{errorMsg}</p>
            {!isLinkFlow && (
              <Button className="mt-5 w-full" onClick={() => navigate({ to: '/open-banking' })}>
                Tillbaka
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
